package com.evoapps.evoshift.widget

import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.shift.ClockException
import com.evoapps.evoshift.domain.usecase.shift.ClockInClockOutUseCase
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull

/**
 * Clocks in or out for the watch (SH-99): the watch has no account, so it asks the phone, which
 * does it as the signed-in person through the same [ClockInClockOutUseCase] as its own button,
 * including the read-only check of an ended plan (SH-65, as MyShiftClock). Then the watch gets a
 * new snapshot ([ShiftWidgetSync.refreshOnce]).
 */
class WatchClockHandler(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val shiftRepository: ShiftRepository,
    private val clockInClockOut: ClockInClockOutUseCase,
    private val plans: ObservePlanActiveUseCase? = null,
    private val offlineAfterMillis: Long = 8_000
) {
    suspend fun handle(request: WatchClockRequest): WatchClockReply {
        val user = getCurrentUserUseCase().first() ?: return WatchClockReply(WatchClockReply.Outcome.SIGNED_OUT)
        val shift = shiftRepository.getShift(request.shiftId).getOrNull()
            ?: return WatchClockReply(WatchClockReply.Outcome.FAILED)
        if (plans != null && !plans.isActive(RosterIds.workplaceIdOf(shift.rosterId))) {
            return WatchClockReply(WatchClockReply.Outcome.PLAN_ENDED)
        }
        // SH-116: a break is recorded by the clockShift function: it needs the network, nothing
        // is kept offline (a failure says so; the watch waits up to 20 s).
        when (request.action) {
            WatchClockRequest.Action.BREAK_START, WatchClockRequest.Action.BREAK_END -> {
                val result = if (request.action == WatchClockRequest.Action.BREAK_START) clockInClockOut.startBreak(shift.id, user.uid)
                else clockInClockOut.endBreak(shift.id, user.uid)
                return WatchClockReply(result.fold(onSuccess = { WatchClockReply.Outcome.DONE }, onFailure = ::outcomeOf))
            }
            else -> Unit
        }
        // Offline, Firestore writes the time to its cache at once but only finishes once online:
        // after [offlineAfterMillis] the time is kept and the watch is told so (it waits 20 s).
        val result = withTimeoutOrNull(offlineAfterMillis) {
            if (request.action == WatchClockRequest.Action.IN) clockInClockOut.clockIn(shift.id, user.uid)
            else clockInClockOut.clockOut(shift.id, user.uid)
        } ?: return WatchClockReply(WatchClockReply.Outcome.SAVED_OFFLINE)
        return WatchClockReply(result.fold(onSuccess = { WatchClockReply.Outcome.DONE }, onFailure = ::outcomeOf))
    }

    private fun outcomeOf(error: Throwable): WatchClockReply.Outcome = when ((error as? ClockException)?.reason) {
        ClockException.Reason.NOT_YOUR_SHIFT -> WatchClockReply.Outcome.NOT_YOUR_SHIFT
        ClockException.Reason.TOO_EARLY -> WatchClockReply.Outcome.TOO_EARLY
        ClockException.Reason.ALREADY_CLOCKED_IN -> WatchClockReply.Outcome.ALREADY_CLOCKED_IN
        ClockException.Reason.NOT_CLOCKED_IN -> WatchClockReply.Outcome.NOT_CLOCKED_IN
        // SH-106: the watch has no location of its own; Block mode asks for the phone.
        ClockException.Reason.LOCATION_REQUIRED -> WatchClockReply.Outcome.LOCATION_REQUIRED
        ClockException.Reason.OUTSIDE_AREA -> WatchClockReply.Outcome.OUTSIDE_AREA
        ClockException.Reason.ALREADY_ON_BREAK, ClockException.Reason.TOO_MANY_BREAKS -> WatchClockReply.Outcome.ALREADY_ON_BREAK
        ClockException.Reason.NOT_ON_BREAK -> WatchClockReply.Outcome.NOT_ON_BREAK
        // SH-134: the watch never clocks in without a shift.
        ClockException.Reason.NO_SHIFT_NOW, ClockException.Reason.ALREADY_DONE, ClockException.Reason.UNDO_TOO_LATE, null -> WatchClockReply.Outcome.FAILED
    }
}
